/**
 * The English legal documents, as supplied by counsel.
 *
 * GENERATED by `scripts/generate-legal.mjs` from the Word files in `terms-to-use/`.
 * Do not edit: a wording change comes from counsel and is applied with `make legal`.
 */

import type { LegalDocument } from "./types";

export const EN_LEGAL_DOCUMENTS: LegalDocument[] = [
  {
    slug: "terms",
    title: "Elyxeer 30.5 Terms of Use",
    subtitle:
      "Rules for subscriber access to the application and digital content",
    sections: [
      {
        number: null,
        heading: null,
        blocks: [
          {
            kind: "paragraph",
            text: "These Terms govern the use of the Elyxeer 30.5 mobile and web application. The application is made available at no additional charge to eligible ELYXEER subscribers and provides access to digital content, communication with the team and subscription management. By using the application, the user confirms that they have read these Terms.",
          },
        ],
      },
      {
        number: "1",
        heading: "Operator and scope",
        blocks: [
          {
            kind: "paragraph",
            text: "1.1 The application is operated by AMPM Labs s.r.o., Company ID No. 23552751, VAT ID No. CZ23552751, with its registered office at Dlouha 730/35, Stare Mesto, 110 00 Prague 1, Czech Republic, registered in the Commercial Register maintained by the Municipal Court in Prague, Section C, File No. 429068 (the Operator). Contact: info@elyxeer.com.",
          },
          {
            kind: "paragraph",
            text: "1.2 At launch, the application is intended primarily for users in the Czech Republic and Slovakia, but it may also be made available in other countries. These Terms apply to every user who has been granted authorised access, irrespective of their country of residence. Purchases and deliveries of physical products are also governed by the ELYXEER online store terms applicable to the relevant order.",
          },
          {
            kind: "paragraph",
            text: "1.3 Access is available to eligible subscribers and to persons individually granted access by the Operator. The Operator does not require a date of birth for access to the application.",
          },
          {
            kind: "paragraph",
            text: "1.4 The availability of the application, individual features, subscriptions, payment options, content and language versions may differ by country. The Operator may restrict availability in a country where required by legal, regulatory, technical, payment or contractual conditions. Rights already acquired by users remain unaffected.",
          },
        ],
      },
      {
        number: "2",
        heading: "Access and user account",
        blocks: [
          {
            kind: "paragraph",
            text: "2.1 Access is linked to the email address recorded for an active ELYXEER subscription in Shopify. The Operator may create an account automatically or, exceptionally, invite a user manually.",
          },
          {
            kind: "paragraph",
            text: "2.2 The account and licence are personal and non-transferable. A user must not share login details, give another person access or circumvent entitlement checks. The user must protect their email account and promptly report suspected misuse.",
          },
          {
            kind: "paragraph",
            text: "2.3 Access continues while the subscription or a manually granted entitlement remains active. When entitlement ends, the Operator may deactivate the account and access. Records required by law or necessary to protect legal rights may be retained for the necessary period.",
          },
          {
            kind: "paragraph",
            text: "2.4 Users may manage their subscription through functions made available in the application. The application does not charge a separate price for digital content. Payment and contractual changes are governed by the online store terms and the information shown before a change is confirmed.",
          },
        ],
      },
      {
        number: "3",
        heading: "Application features",
        blocks: [
          {
            kind: "paragraph",
            text: "3.1 The application offers in particular:",
          },
          {
            kind: "bullets",
            items: [
              "monthly live broadcasts for subscribers, with questions for the team or guests;",
              "private questions and answers between the user and an authorised administrator;",
              "articles, podcasts, videos, protocols and other educational content about supplementation and a healthy lifestyle;",
              "content filtering and search;",
              "display and management of subscription information to the extent offered in the application.",
            ],
          },
          {
            kind: "paragraph",
            text: "3.2 The Operator may change the selection, order and availability of content. Continuous availability of every item, a particular guest or a particular broadcast date is not guaranteed. A material restriction of the service as a whole will be communicated in an appropriate manner.",
          },
        ],
      },
      {
        number: "4",
        heading: "Health and professional disclaimer",
        blocks: [
          {
            kind: "paragraph",
            text: "4.1 Neither the Operator nor Elyxeer 30.5 is a healthcare facility or healthcare provider. We do not diagnose or monitor health conditions and do not provide treatment, individual medical prevention, medical advice, treatment plans or healthcare. The application focuses exclusively on general information and education about supplementation and a healthy lifestyle.",
          },
          {
            kind: "paragraph",
            text: "4.2 Users must not start, change or stop treatment or medication on the basis of application content. Before making a decision that may affect health, users should consider their individual circumstances and consult an appropriate qualified professional. In an acute or serious situation, users must promptly seek appropriate medical assistance.",
          },
          {
            kind: "paragraph",
            text: "4.3 Team responses are general and based only on information supplied by the user. Users must not send medical records, national identification numbers, identity-document numbers, payment details or other information unnecessary for the question.",
          },
        ],
      },
      {
        number: "5",
        heading: "Private questions and communication rules",
        blocks: [
          {
            kind: "paragraph",
            text: "5.1 A question and its answer are visible only to the user and authorised members or service providers of the Operator team. Other subscribers cannot see them. The Operator does not guarantee an immediate response and the chat must not be used for urgent matters.",
          },
          {
            kind: "paragraph",
            text: "5.2 Users must not submit unlawful, threatening, abusive, discriminatory, sexually explicit, fraudulent or harmful content; infringe third-party rights; impersonate another person; distribute malware, spam or advertising; or use the service to harass anyone.",
          },
          {
            kind: "paragraph",
            text: "5.3 The Operator may remove prohibited content, restrict communication, suspend an account and, in serious cases, report a matter to the competent authorities. Concerns may be reported to the contact email and will be assessed without undue delay.",
          },
        ],
      },
      {
        number: "6",
        heading: "Live broadcasts",
        blocks: [
          {
            kind: "paragraph",
            text: "6.1 Users must follow moderator instructions and respect the rights of other participants. The Operator may decline, shorten or edit a question to remove personal data or inappropriate content without changing its substance.",
          },
          {
            kind: "paragraph",
            text: "6.2 Users must not record, retransmit, communicate to the public or share a broadcast with another person. If the Operator records a particular broadcast, participants will be informed in advance about the scope and use of the recording.",
          },
        ],
      },
      {
        number: "7",
        heading: "Intellectual property and licence",
        blocks: [
          {
            kind: "paragraph",
            text: "7.1 Text, graphics, video, audio, protocols, database structure, branding and other content are protected by law. Rights belong to the Operator or its licensors.",
          },
          {
            kind: "paragraph",
            text: "7.2 The user receives a limited, non-exclusive, non-transferable and revocable right to use content for personal, non-commercial purposes while authorised access continues. Content must not be downloaded unless the application expressly provides an authorised offline function.",
          },
          {
            kind: "paragraph",
            text: "7.3 Without prior written consent, users must not copy, photograph or record content for redistribution; publish, sell or provide it to third parties; systematically extract or scrape it; modify it; use it to create databases or training data; or circumvent technical restrictions. Rights that cannot legally be excluded remain unaffected.",
          },
        ],
      },
      {
        number: "8",
        heading: "Operation and liability",
        blocks: [
          {
            kind: "paragraph",
            text: "8.1 The Operator may perform maintenance, security work and updates. Where reasonably possible, users will be informed of planned longer interruptions. Users are responsible for a supported device, current operating system and secure connection.",
          },
          {
            kind: "paragraph",
            text: "8.2 The Operator is liable to the extent required by mandatory law. Nothing in these Terms excludes statutory consumer rights, liability for intent or gross negligence, or liability that cannot validly be limited.",
          },
          {
            kind: "paragraph",
            text: "8.3 The Operator provides updates, including security updates, necessary for proper and secure use. Users should install them within a reasonable time. The Operator is not liable for a defect caused solely by failure to install a properly notified update where the user was informed of its availability and the consequences of not installing it.",
          },
          {
            kind: "paragraph",
            text: "8.4 Technical defects should be reported to the contact email with the device, application version and problem description. Users may in particular request that a defect be remedied; all further mandatory rights relating to defective digital services remain unaffected.",
          },
        ],
      },
      {
        number: "9",
        heading: "Suspension and termination",
        blocks: [
          {
            kind: "paragraph",
            text: "9.1 Access may be proportionately suspended where entitlement ends, security is threatened, an account is shared, the service is misused or these Terms are materially breached. Where circumstances permit, the Operator will warn the user and allow an opportunity to remedy the breach.",
          },
          {
            kind: "paragraph",
            text: "9.2 A subscription may be cancelled as stated in the online store terms or through available subscription management. Cancellation ends future deliveries and, after the relevant entitlement period, access to the application.",
          },
          {
            kind: "paragraph",
            text: "9.3 Requests concerning an account or personal data may be submitted through the application, where available, or to the contact email. Details appear in the Privacy Policy and Account Deletion and Data Erasure Instructions.",
          },
        ],
      },
      {
        number: "10",
        heading: "Complaints and disputes",
        blocks: [
          {
            kind: "paragraph",
            text: "10.1 Complaints may be sent to the contact email. Consumers may also use alternative dispute resolution before the Czech Trade Inspection Authority at www.coi.cz. This does not affect remedies available in the state of the consumer’s habitual residence.",
          },
        ],
      },
      {
        number: "11",
        heading: "Final provisions",
        blocks: [
          {
            kind: "paragraph",
            text: "11.1 These legal relations are governed by Czech law. A consumer is not deprived of the protection of mandatory provisions of the law of their habitual residence.",
          },
          {
            kind: "paragraph",
            text: "11.2 The Operator may reasonably amend these Terms or the digital service due to changes in law, features, technology or security. A change will not be made without a valid reason or impose additional cost on the user. A non-minor change that may negatively affect access or use will be clearly notified in advance together with information about applicable statutory rights. Existing rights remain unaffected.",
          },
          {
            kind: "paragraph",
            text: "11.3 These Terms take effect on 20 September 2026.",
          },
        ],
      },
    ],
  },
  {
    slug: "privacy",
    title: "Elyxeer 30.5 Privacy Policy",
    subtitle: "Information for users of the mobile and web application",
    sections: [
      {
        number: null,
        heading: null,
        blocks: [
          {
            kind: "paragraph",
            text: "This Policy explains what personal data we process when providing and operating Elyxeer 30.5, why we process it, how long we keep it and the rights available to users.",
          },
        ],
      },
      {
        number: "1",
        heading: "Controller and contact",
        blocks: [
          {
            kind: "paragraph",
            text: "The data controller is AMPM Labs s.r.o., Company ID No. 23552751, VAT ID No. CZ23552751, with its registered office at Dlouha 730/35, Stare Mesto, 110 00 Prague 1, Czech Republic, registered in the Commercial Register maintained by the Municipal Court in Prague, Section C, File No. 429068. For privacy matters, contact info@elyxeer.com.",
          },
        ],
      },
      {
        number: "2",
        heading: "Processing activities",
        blocks: [
          {
            kind: "table",
            columns: [
              "Purpose",
              "Data",
              "Legal basis",
              "Basic retention period",
            ],
            rows: [
              [
                "Account creation and administration",
                "email, internal identifier, entitlement status and login time; no date of birth is required",
                "performance of a contract",
                "active account period, then generally 3 years to protect legal rights",
              ],
              [
                "Subscription verification and management",
                "email, customer and subscription identifiers, status, and necessary order and administration data",
                "performance of a contract; legal obligation for accounting records",
                "under the online store terms and statutory retention periods",
              ],
              [
                "Content delivery",
                "access history, selected filters and technical playback data",
                "performance of a contract",
                "account period; aggregate data may remain anonymous",
              ],
              [
                "Private chat and support",
                "question and answer text, email, communication time and any attachments",
                "performance of a contract; legitimate interests in support and protection of rights",
                "until resolved and generally up to 3 years thereafter, unless longer retention is legally necessary",
              ],
              [
                "Live broadcasts",
                "login, question, user-chosen name or label and technical transmission data",
                "performance of a contract",
                "for the broadcast; recordings only as stated in advance",
              ],
              [
                "Security and operation",
                "IP address, device, app version, system and security logs, error reports",
                "legitimate interests in secure operation",
                "generally up to 6 months, longer for incident resolution",
              ],
              [
                "Usage analytics",
                "in-app events, sections visited, session duration and approximate device technical data",
                "consent, unless strictly necessary operational analytics",
                "according to tool settings, no longer than 14 months",
              ],
              [
                "Legal claims and compliance",
                "relevant accounting, contractual and communication records",
                "legal obligation; legitimate interests",
                "statutory period or limitation period",
              ],
            ],
          },
        ],
      },
      {
        number: "3",
        heading: "Shopify connection",
        blocks: [
          {
            kind: "paragraph",
            text: "The application is automatically connected to the Shopify online store account. It receives primarily the email address, customer identifier and information on whether a subscription is active. Where subscription management is available in the application, further data necessary to make the requested change may be displayed or transmitted. Payment information is processed by the relevant payment service provider; complete payment-card details should not be stored in the application.",
          },
        ],
      },
      {
        number: "4",
        heading: "Health and other sensitive data",
        blocks: [
          {
            kind: "paragraph",
            text: "Neither the Operator nor the application is a healthcare facility or healthcare provider. The application concerns general information about supplementation and a healthy lifestyle and is not intended for diagnosis, treatment or health monitoring. It does not collect data from Apple Health, Google Health Connect, wearables or medical devices. A user may voluntarily include health information in a free-text question. Users should provide only what is necessary for a general question and must not send medical records or unnecessary identifiers.",
          },
          {
            kind: "paragraph",
            text: "If a user wishes to include health information, the application must obtain separate explicit consent under Article 9(2)(a) GDPR before submission. We will process the data only as necessary to handle the question and with appropriate safeguards. Consent may be withdrawn at any time without affecting prior lawful processing. Without consent, health data must not be included in a question.",
          },
        ],
      },
      {
        number: "5",
        heading: "Analytics and device technologies",
        blocks: [
          {
            kind: "paragraph",
            text: "We analyse use to understand which parts of the application are used and where technical problems occur. Strictly necessary technical records support operation and security. Non-essential storage of, or access to, information on a device is activated only after consent. Consent may be refused or later withdrawn in privacy settings without affecting basic access.",
          },
          {
            kind: "paragraph",
            text: "We do not use application personal data for behavioural advertising or tracking across third-party applications unless users are first clearly informed of that new purpose and any required consent is obtained.",
          },
        ],
      },
      {
        number: "6",
        heading: "Recipients and service providers",
        blocks: [
          {
            kind: "bullets",
            items: [
              "Shopify and related online store and subscription-management providers;",
              "hosting, database, security, technical-support and development providers;",
              "video, chat, email and push-notification providers used for actual features;",
              "analytics and technical error-reporting providers;",
              "legal, accounting and other professional advisers, and public authorities where required by law.",
            ],
          },
          {
            kind: "paragraph",
            text: "Service providers process data under contract and our instructions unless they independently determine their purposes as controllers. Current information about significant providers is available on request and, where relevant, in the application privacy settings.",
          },
        ],
      },
      {
        number: "7",
        heading: "Transfers outside the European Economic Area",
        blocks: [
          {
            kind: "paragraph",
            text: "Some technology providers may process data outside the European Economic Area. Transfers occur only on the basis of a European Commission adequacy decision or appropriate safeguards, particularly standard contractual clauses and, where necessary, supplementary measures. Information about the relevant mechanism is available through the contact email.",
          },
        ],
      },
      {
        number: "8",
        heading: "Automated decisions",
        blocks: [
          {
            kind: "paragraph",
            text: "Access may be automatically activated or deactivated according to subscription status. This is a technical step in performing the contract. We do not make other decisions based solely on automated processing that produce legal or similarly significant effects.",
          },
        ],
      },
      {
        number: "9",
        heading: "User rights",
        blocks: [
          {
            kind: "bullets",
            items: [
              "access to personal data and a copy of it;",
              "correction of inaccurate or incomplete data;",
              "erasure and restriction where statutory conditions are met;",
              "portability of data processed automatically on the basis of consent or contract;",
              "objection to processing based on legitimate interests;",
              "withdrawal of consent at any time without affecting earlier lawful processing;",
              "a complaint to a data-protection supervisory authority, particularly in the country of habitual residence, place of work or alleged infringement. In the Czech Republic this is the Office for Personal Data Protection; in Slovakia, the Office for Personal Data Protection of the Slovak Republic.",
            ],
          },
          {
            kind: "paragraph",
            text: "Requests may be sent to info@elyxeer.com. We may reasonably verify the requester’s identity. We will respond without undue delay, generally within one month.",
          },
        ],
      },
      {
        number: "10",
        heading: "Account deletion",
        blocks: [
          {
            kind: "paragraph",
            text: "Users may initiate account deletion in the application or through the contact email. Deleting the application account and cancelling the commercial subscription are separate actions unless the interface expressly confirms both. The consequences will be shown before confirmation. Data required for a legal obligation, security or legal claims will be retained until that reason ends.",
          },
        ],
      },
      {
        number: "11",
        heading: "Security and changes",
        blocks: [
          {
            kind: "paragraph",
            text: "We use appropriate technical and organisational measures, including access controls, secure transmission, entitlement records, backups and incident-response procedures. No system can be described as absolutely secure.",
          },
          {
            kind: "paragraph",
            text: "We may update this Policy when features, providers or legal duties change. A material change will be communicated in the application or by email before it takes effect where appropriate. The current version will remain publicly available without login.",
          },
          {
            kind: "paragraph",
            text: "This Policy takes effect on 20 September 2026.",
          },
        ],
      },
    ],
  },
  {
    slug: "account-deletion",
    title: "Elyxeer 30.5 Account Deletion and Data Erasure",
    subtitle: "Public instructions for application users",
    sections: [
      {
        number: null,
        heading: null,
        blocks: [
          {
            kind: "paragraph",
            text: "A user may request deletion of their Elyxeer 30.5 account and personal data that is no longer needed. This page should be publicly available without login and linked from Google Play Console and the application.",
          },
        ],
      },
      {
        number: null,
        heading: "How to submit a request",
        blocks: [
          {
            kind: "steps",
            items: [
              "In the application, open Settings, select Account and then Delete Account and Data.",
              "If you cannot sign in, write from the email address linked to the account to info@elyxeer.com with “Elyxeer 30.5 account deletion” in the subject line.",
              "Confirm the request using the method we send you. We may reasonably verify identity to prevent misuse.",
              "Before final confirmation, the application will separately explain the effect on application access and on the ELYXEER subscription.",
            ],
          },
        ],
      },
      {
        number: null,
        heading: "What will be deleted",
        blocks: [
          {
            kind: "bullets",
            items: [
              "the application profile and access entitlement;",
              "account-linked usage history that is no longer needed;",
              "private questions and other account content where no retention ground remains;",
              "device tokens and server-side application settings.",
            ],
          },
        ],
      },
      {
        number: null,
        heading: "What may be retained",
        blocks: [
          {
            kind: "paragraph",
            text: "Accounting and order records, payment evidence, and records necessary for complaints, fraud prevention, security or legal claims will be retained only for the period required by law or necessary for that purpose. They will no longer be used for ordinary operation of the deleted account.",
          },
        ],
      },
      {
        number: null,
        heading: "Relationship to the subscription",
        blocks: [
          {
            kind: "paragraph",
            text: "Deleting the application account and cancelling the subscription are two different steps. Account deletion alone does not cancel future payments or deliveries unless the user also expressly confirms subscription cancellation. If the subscription is cancelled, application access ends after the relevant entitlement period.",
          },
        ],
      },
      {
        number: null,
        heading: "Timing and contact",
        blocks: [
          {
            kind: "paragraph",
            text: "We will process the request without undue delay, generally within one month. We will confirm completion or explain any limited retention. Contact: info@elyxeer.com.",
          },
        ],
      },
    ],
  },
];
